import type { ZodTypeAny } from 'zod';

import type { EsutdMethod } from './esutd';
import {
  confirmReadingSchema,
  createWaybillCargoSchema,
  entityActionSchema,
  medicalExaminationSchema,
  returnDateTimeSchema,
  signWaybillSchema,
  takeTechnicalResponsibilitySchema,
  technicalInspectionPostTripSchema,
  technicalInspectionPreTripSchema,
} from './esutd-bodies';

/**
 * Стадии цепочки ЕСУТД и их сопоставление с состоянием документа на стороне ЕСУТД.
 *
 * Стадия — это НАШЕ понятие: один исходящий вызов. `progressStatus` — понятие ЕСУТД:
 * кого документ ждёт. Смешивать их нельзя: методов шесть, вызовов девять.
 */

/**
 * `EWaybillCargoProgressStatus` — восемь значений грузового листа, в порядке прохождения:
 * `MEDICAL_WORKER → MECHANIC → CARRIER → DRIVER_WORKER → SECOND_TIME_DRIVER_WORKER →
 * SECOND_TIME_CARRIER → SECOND_TIME_MEDICAL_WORKER → SECOND_TIME_MECHANIC`.
 *
 * Отличия от пассажирского листа: нет `BUS_STATION_DISPATCHER` (он возникает только при
 * `IRREGULAR`), а отметка возврата принадлежит `SECOND_TIME_CARRIER`, а не `ORGANIZATION`.
 * `ORGANIZATION` и `SECOND_TIME_ORGANIZATION` грузовой лист не проходит вовсе — их убрали
 * и из опубликованного enum. `FINISHED` тоже не значение строки: документ завершается
 * тем, что последняя строка переходит в согласованное состояние.
 */
export const ESUTD_PROGRESS_STATUSES = [
  'MEDICAL_WORKER',
  'MECHANIC',
  'CARRIER',
  'DRIVER_WORKER',
  'SECOND_TIME_DRIVER_WORKER',
  'SECOND_TIME_CARRIER',
  'SECOND_TIME_MEDICAL_WORKER',
  'SECOND_TIME_MECHANIC',
] as const;
export type EsutdProgressStatus = (typeof ESUTD_PROGRESS_STATUSES)[number];

/** `EWaybillStatus` — вторая, независимая ось относительно `progressStatus`. */
export const ESUTD_DOCUMENT_STATUSES = [
  'DRAFT',
  'IN_PROGRESS',
  'COMPLETED',
  'CANCELED',
  'REVOKED',
  /** Ставит джоба автоэкспирации; сами мы его не вызываем, но принять обязаны. */
  'EXPIRED',
] as const;
export type EsutdDocumentStatus = (typeof ESUTD_DOCUMENT_STATUSES)[number];

/**
 * Наши стадии в терминах ЕСУТД. `CARRIER_EDIT` здесь нет: правка выданного документа
 * (`EDIT_IN_PROGRESS_WAYBILL_CARGO`) в цепочку не входит и делается в веб-кабинете.
 */
export const ESUTD_STAGES = [
  'CREATE',
  'MED_PRE',
  'TECH_PRE',
  'SIGN_CARRIER',
  'READING_PRE',
  'READING_POST',
  'RETURN_MARK',
  'MED_POST',
  'TECH_POST',
  'REVOKE',
  'TAKE_TECH_RESPONSIBILITY',
] as const;
export type EsutdStage = (typeof ESUTD_STAGES)[number];

/**
 * Штатная цепочка грузового ЭПЛ: девять вызовов, шесть различных методов.
 * `REVOKE` и `TAKE_TECH_RESPONSIBILITY` в неё не входят — это выходы из цепочки.
 */
export const ESUTD_CHAIN_STAGES: readonly EsutdStage[] = [
  'CREATE',
  'MED_PRE',
  'TECH_PRE',
  'SIGN_CARRIER',
  'READING_PRE',
  'READING_POST',
  'RETURN_MARK',
  'MED_POST',
  'TECH_POST',
];

/**
 * Сколько вызовов нужно на один ЭПЛ: `7 + 2N`. Круги подтверждения водителей
 * умножаются на число водителей — стадия двигается только когда закрыта
 * последняя `WAITING`-строка организации.
 */
export function esutdCallCount(driverCount: number): number {
  return ESUTD_CHAIN_STAGES.length - 2 + 2 * Math.max(driverCount, 1);
}

export const STAGE_TO_METHOD: Record<EsutdStage, EsutdMethod> = {
  CREATE: 'CREATE_WAYBILL_CARGO',
  MED_PRE: 'SIGN_WAYBILL_CARGO_MEDICAL_EXAMINATION',
  MED_POST: 'SIGN_WAYBILL_CARGO_MEDICAL_EXAMINATION',
  TECH_PRE: 'SIGN_WAYBILL_CARGO_TECHNICAL_INSPECTION',
  TECH_POST: 'SIGN_WAYBILL_CARGO_TECHNICAL_INSPECTION',
  SIGN_CARRIER: 'SIGN_WAYBILL_CARGO',
  READING_PRE: 'CONFIRM_READING_WAYBILL_CARGO',
  READING_POST: 'CONFIRM_READING_WAYBILL_CARGO',
  RETURN_MARK: 'SET_RETURN_DATE_TIME_WAYBILL_CARGO',
  REVOKE: 'REVOKE_WAYBILL_CARGO',
  TAKE_TECH_RESPONSIBILITY: 'TAKE_TECHNICAL_RESPONSIBILITY_WAYBILL_CARGO',
};

/**
 * Какую стадию ЕСУТД ждёт от нас при данном `progressStatus`.
 * `null` — стадия недостижима для нашего профиля либо документ уже никого не ждёт.
 */
export const PROGRESS_TO_EXPECTED_STAGE: Record<EsutdProgressStatus, EsutdStage | null> = {
  MEDICAL_WORKER: 'MED_PRE',
  MECHANIC: 'TECH_PRE',
  CARRIER: 'SIGN_CARRIER',
  DRIVER_WORKER: 'READING_PRE',
  SECOND_TIME_DRIVER_WORKER: 'READING_POST',
  SECOND_TIME_CARRIER: 'RETURN_MARK',
  SECOND_TIME_MEDICAL_WORKER: 'MED_POST',
  SECOND_TIME_MECHANIC: 'TECH_POST',
};

/**
 * Нужна ли на стадии ЭЦП конкретного физлица.
 *
 * Именно эта таблица не даёт требовать подпись там, где её нет: подтверждения
 * водителя, отметка возврата и ПОСЛЕрейсовый техосмотр подписи не требуют
 * (`checkSignature` в техосмотре вызывается только при `preTrip`).
 *
 * `MED_POST` = true: медик подписывает и послерейсовый осмотр. В legacy-режиме
 * при отказе хотя бы по одному водителю проверка подписи пропускается, но
 * канонический режим требует её и там — посылаем подпись всегда, это надмножество.
 */
export const STAGE_REQUIRES_SIGNATURE: Record<EsutdStage, boolean> = {
  CREATE: false,
  MED_PRE: true,
  TECH_PRE: true,
  SIGN_CARRIER: true,
  READING_PRE: false,
  READING_POST: false,
  RETURN_MARK: false,
  MED_POST: true,
  TECH_POST: false,
  REVOKE: false,
  TAKE_TECH_RESPONSIBILITY: false,
};

/**
 * Стадии, которыми подпись роли попадает в ЕСУТД.
 *
 * Нужны, чтобы сопоставить нашу отметку о подписи с судьбой стадии у них. Порядок значим:
 * первая стадия списка — основная, по ней и судим о состоянии подписи.
 *
 * `MED_POST` в список медработника НЕ входит: послерейсовый осмотр он подписывает отдельно,
 * и смешивать их значило бы гасить предрейсовую подпись послерейсовой ошибкой.
 * `TAKE_TECH_RESPONSIBILITY` у механика есть: это аварийный выход, когда механика нет и
 * техответственность берёт уполномоченное лицо — стадия механика при этом закрывается.
 */
export const SIGNATURE_ROLE_STAGES = {
  DISPATCHER: ['SIGN_CARRIER'],
  MECHANIC: ['TECH_PRE', 'TAKE_TECH_RESPONSIBILITY'],
  MEDIC: ['MED_PRE'],
  DRIVER: ['READING_PRE'],
} as const satisfies Record<string, readonly EsutdStage[]>;

/** Схема тела по стадии: пред- и послерейсовый техосмотр принимают разные поля. */
export const ESUTD_STAGE_SCHEMAS: Record<EsutdStage, ZodTypeAny> = {
  CREATE: createWaybillCargoSchema,
  MED_PRE: medicalExaminationSchema,
  MED_POST: medicalExaminationSchema,
  TECH_PRE: technicalInspectionPreTripSchema,
  TECH_POST: technicalInspectionPostTripSchema,
  SIGN_CARRIER: signWaybillSchema,
  READING_PRE: confirmReadingSchema,
  READING_POST: confirmReadingSchema,
  RETURN_MARK: returnDateTimeSchema,
  REVOKE: entityActionSchema,
  TAKE_TECH_RESPONSIBILITY: takeTechnicalResponsibilitySchema,
};

export const ESUTD_STAGE_LABELS: Record<EsutdStage, string> = {
  CREATE: 'Создание ЭПЛ',
  MED_PRE: 'Предрейсовый медосмотр',
  TECH_PRE: 'Предрейсовый техосмотр',
  SIGN_CARRIER: 'Подпись перевозчика (ЭЦП ЮЛ)',
  READING_PRE: 'Подтверждение водителем при получении',
  READING_POST: 'Подтверждение водителем при сдаче',
  RETURN_MARK: 'Отметка возврата',
  MED_POST: 'Послерейсовый медосмотр',
  TECH_POST: 'Послерейсовый техосмотр',
  REVOKE: 'Отзыв ЭПЛ',
  TAKE_TECH_RESPONSIBILITY: 'Перевозчик берёт техответственность',
};

export const ESUTD_PROGRESS_LABELS: Record<EsutdProgressStatus, string> = {
  MEDICAL_WORKER: 'Ожидает предрейсовый медосмотр',
  MECHANIC: 'Ожидает предрейсовый техосмотр',
  CARRIER: 'Ожидает подпись перевозчика',
  DRIVER_WORKER: 'Ожидает подтверждение водителя',
  SECOND_TIME_DRIVER_WORKER: 'Ожидает повторное подтверждение водителя',
  SECOND_TIME_CARRIER: 'Ожидает отметку возврата',
  SECOND_TIME_MEDICAL_WORKER: 'Ожидает послерейсовый медосмотр',
  SECOND_TIME_MECHANIC: 'Ожидает послерейсовый техосмотр',
};

export const ESUTD_DOCUMENT_STATUS_LABELS: Record<EsutdDocumentStatus, string> = {
  DRAFT: 'Черновик (отклонён на предрейсовом осмотре)',
  IN_PROGRESS: 'В работе',
  COMPLETED: 'Завершён',
  CANCELED: 'Аннулирован в ЕСУТД',
  REVOKED: 'Отозван',
  EXPIRED: 'Закрыт по истечении срока',
};

/** Единые подписи сырых значений ЕСУТД для интерфейса; неизвестное показываем как есть. */
export const ESUTD_SYNC_LABELS: Readonly<Record<string, string>> = {
  ...ESUTD_PROGRESS_LABELS,
  ...ESUTD_DOCUMENT_STATUS_LABELS,
};

export function esutdSyncLabel(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }
  return ESUTD_SYNC_LABELS[value] ?? value;
}

/** Результат сверки нашего состояния со стадией ЕСУТД. */
export type EsutdReconcileVerdict = 'MATCH' | 'AHEAD' | 'BEHIND' | 'UNKNOWN';

/**
 * Сверяет стадию, которую ЕСУТД ждёт от нас, с той, которую собираемся отправить.
 * `AHEAD` — ЕСУТД дальше нас (наш переход уже применён), `BEHIND` — мы забежали вперёд.
 */
export function reconcileStage(
  ourNextStage: EsutdStage | null,
  esutdProgress: string | null | undefined,
): EsutdReconcileVerdict {
  if (!esutdProgress || !(esutdProgress in PROGRESS_TO_EXPECTED_STAGE)) {
    return 'UNKNOWN';
  }
  const expected = PROGRESS_TO_EXPECTED_STAGE[esutdProgress as EsutdProgressStatus];
  if (!expected || !ourNextStage) {
    return 'UNKNOWN';
  }
  if (expected === ourNextStage) {
    return 'MATCH';
  }
  const ourIndex = ESUTD_CHAIN_STAGES.indexOf(ourNextStage);
  const theirIndex = ESUTD_CHAIN_STAGES.indexOf(expected);
  if (ourIndex < 0 || theirIndex < 0) {
    return 'UNKNOWN';
  }
  return theirIndex > ourIndex ? 'AHEAD' : 'BEHIND';
}
